<script lang="ts" setup>
import { computed, inject, ref } from 'vue'

import type { MultiSelectItem } from './types'

const props = withDefaults(
  defineProps<{
    item: MultiSelectItem
    parentPath?: (string | number)[]
  }>(),
  {
    parentPath: () => [],
  },
)

interface ItemsContext {
  updateItem: (path: (string | number)[], selected: boolean) => void
}

const emit = defineEmits(['change'])

const { updateItem } = inject<ItemsContext>('items')

const isSelected = computed(() => props.item.selected)
const hasChildren = computed(() => props.item.children && props.item.children.length > 0)
const path = computed(() => [...props.parentPath, props.item.value])

const childrenRefs = ref([])

function onSelect(selected) {
  updateItem(path.value, selected.target.checked)
  updateParent()
  updateChildren(selected.target.checked)
}

function onChildSelect() {
  updateItem(
    path.value,
    props.item.children.every((child) => child.selected),
  )
  updateParent()
}

function updateParent() {
  emit('change')
}

function updateChildren(selected) {
  if (!props.item.children) return
  childrenRefs.value.forEach((child) => {
    updateItem(child.path, selected)
    child.updateChildren(selected)
  })
}

defineExpose({
  path,
  updateChildren,
})
</script>

<template>
  <div
    class="item"
    :class="{ option: !hasChildren, 'col-6': !hasChildren, 'col-12': hasChildren }"
  >
    <label class="mb-1 ms-3">
      <input
        type="checkbox"
        :checked="isSelected"
        @change="onSelect"
      >
      {{ item.label }}
    </label>
    <div
      v-if="hasChildren"
      class="row ms-1"
    >
      <SancareMultiselectItem
        v-for="child in item.children"
        ref="childrenRefs"
        :key="child.value"
        :parent-path="path"
        :item="child"
        @change="onChildSelect"
      />
    </div>
  </div>
</template>
